import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Trash2, Upload, FileText, Download, Eye } from "lucide-react";
import { api, formatApiError, openFile } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { SignaturePad } from "../../components/SignaturePad";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { ClientDetailDialog } from "../../components/ClientDetailDialog";

const STEPS = ["Personal Info", "Dependents", "Income", "Prior Year", "Signature", "Documents"];

const INCOME_SOURCES = [
  { key: "w2", label: "W-2 Wages" },
  { key: "1099", label: "1099 Income" },
  { key: "self_employed", label: "Self-Employment" },
  { key: "rental", label: "Rental Income" },
  { key: "investment", label: "Investments / Dividends" },
  { key: "retirement", label: "Retirement / Pension" },
  { key: "other", label: "Other Income" },
];

const DOC_CATEGORIES = [
  { value: "w2", label: "W-2" },
  { value: "1099", label: "1099" },
  { value: "id", label: "Photo ID" },
  { value: "prior_return", label: "Prior Year Return" },
  { value: "other", label: "Other Document" },
];

const emptyPersonal = { firstName: "", lastName: "", dob: "", ssnLast4: "", phone: "", street: "", city: "", state: "UT", zip: "", filingStatus: "single" };

export default function Intake() {
  const { user } = useAuth();
  const isStaff = user?.role === "staff" || user?.role === "admin";
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [personal, setPersonal] = useState(emptyPersonal);
  const [dependents, setDependents] = useState([]);
  const [income, setIncome] = useState({ sources: [], details: "" });
  const [priorYear, setPriorYear] = useState({ filedLastYear: "", result: "", preparer: "", notes: "" });
  const [signature, setSignature] = useState({ fullName: "", agreed: false, dataUrl: null });
  const [documents, setDocuments] = useState([]);
  const [category, setCategory] = useState("w2");
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.get("/intakes/mine").then((r) => {
      if (r.data?.personal) {
        setPersonal({ ...emptyPersonal, ...r.data.personal });
        setDependents(r.data.dependents || []);
        setIncome({ sources: [], details: "", ...r.data.income });
        setPriorYear({ filedLastYear: "", result: "", preparer: "", notes: "", ...r.data.prior_year });
        if (r.data.signature?.fullName) setSignature((s) => ({ ...s, fullName: r.data.signature.fullName, dataUrl: r.data.signature.dataUrl || null }));
        setSubmitted(true);
      }
    }).catch(() => {});
    api.get("/documents").then((r) => setDocuments(r.data)).catch(() => {});
  }, []);

  const setP = (k) => (e) => setPersonal((p) => ({ ...p, [k]: e.target.value }));
  const setPY = (k) => (e) => setPriorYear((p) => ({ ...p, [k]: e.target.value }));

  const toggleSource = (key) => {
    setIncome((i) => ({
      ...i,
      sources: i.sources.includes(key) ? i.sources.filter((s) => s !== key) : [...i.sources, key],
    }));
  };

  const addDependent = () => setDependents((d) => [...d, { name: "", relationship: "", dob: "" }]);
  const updateDependent = (idx, key, value) => setDependents((d) => d.map((dep, i) => (i === idx ? { ...dep, [key]: value } : dep)));
  const removeDependent = (idx) => setDependents((d) => d.filter((_, i) => i !== idx));

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    form.append("category", category);
    try {
      const { data } = await api.post("/documents", form, { headers: { "Content-Type": "multipart/form-data" } });
      setDocuments((d) => [...d, data]);
      toast.success(`${file.name} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err, "Upload failed"));
    } finally {
      e.target.value = "";
    }
  };

  const submit = async () => {
    if (!signature.dataUrl) {
      toast.error("Please add your signature on the Signature step");
      setStep(4);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/intakes", {
        personal, dependents, income, prior_year: priorYear,
        signature: { fullName: signature.fullName, dataUrl: signature.dataUrl, signedAt: new Date().toISOString() },
      });
      setSubmitted(true);
      toast.success("Intake submitted — confirmation email sent");
    } catch (err) {
      toast.error(formatApiError(err, "Failed to submit intake"));
    } finally {
      setSubmitting(false);
    }
  };

  if (isStaff) return <StaffIntakes />;

  return (
    <div data-testid="intake-page" className="max-w-2xl mx-auto animate-fade-up">
      <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-medium">Client onboarding</p>
      <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight mt-1">Tax Intake Form</h1>
      {submitted && (
        <p className="mt-2 text-sm text-primary font-medium" data-testid="intake-submitted-note">
          Your intake has been submitted. You can update it below and resubmit anytime.
        </p>
      )}

      <div className="flex gap-1.5 mt-8 mb-8" data-testid="wizard-progress">
        {STEPS.map((s, i) => (
          <button key={s} type="button" onClick={() => setStep(i)} data-testid={`wizard-step-${i}`}
            className="flex-1 group text-left">
            <div className={`h-1.5 rounded-full transition-colors duration-200 ${i <= step ? "bg-primary" : "bg-border group-hover:bg-secondary"}`} />
            <p className={`text-[10px] mt-1.5 hidden sm:block ${i === step ? "text-foreground font-medium" : "text-muted-foreground"}`}>{s}</p>
          </button>
        ))}
      </div>

      <Card>
        <CardContent className="p-6 md:p-8">
          {step === 0 && (
            <div className="space-y-4" data-testid="step-personal">
              <h2 className="font-heading text-2xl font-semibold">Personal Information</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>First name</Label><Input data-testid="intake-first-name" value={personal.firstName} onChange={setP("firstName")} /></div>
                <div className="space-y-1.5"><Label>Last name</Label><Input data-testid="intake-last-name" value={personal.lastName} onChange={setP("lastName")} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Date of birth</Label><Input data-testid="intake-dob" type="date" value={personal.dob} onChange={setP("dob")} /></div>
                <div className="space-y-1.5"><Label>SSN (last 4)</Label><Input data-testid="intake-ssn" maxLength={4} placeholder="••••" value={personal.ssnLast4} onChange={setP("ssnLast4")} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="intake-phone" type="tel" value={personal.phone} onChange={setP("phone")} /></div>
                <div className="space-y-1.5">
                  <Label>Filing status</Label>
                  <Select value={personal.filingStatus} onValueChange={(v) => setPersonal((p) => ({ ...p, filingStatus: v }))}>
                    <SelectTrigger data-testid="intake-filing-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="married_joint">Married Filing Jointly</SelectItem>
                      <SelectItem value="married_separate">Married Filing Separately</SelectItem>
                      <SelectItem value="head_of_household">Head of Household</SelectItem>
                      <SelectItem value="widow">Qualifying Widow(er)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5"><Label>Street address</Label><Input data-testid="intake-street" value={personal.street} onChange={setP("street")} /></div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1.5"><Label>City</Label><Input data-testid="intake-city" value={personal.city} onChange={setP("city")} /></div>
                <div className="space-y-1.5"><Label>State</Label><Input data-testid="intake-state" value={personal.state} onChange={setP("state")} /></div>
                <div className="space-y-1.5"><Label>ZIP</Label><Input data-testid="intake-zip" value={personal.zip} onChange={setP("zip")} /></div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="step-dependents">
              <h2 className="font-heading text-2xl font-semibold">Dependents</h2>
              <p className="text-sm text-muted-foreground">Add anyone you claim as a dependent. Skip if none.</p>
              {dependents.map((dep, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_auto] sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end border border-border rounded-md p-4" data-testid={`dependent-row-${i}`}>
                  <div className="space-y-1.5"><Label>Full name</Label><Input data-testid={`dependent-name-${i}`} value={dep.name} onChange={(e) => updateDependent(i, "name", e.target.value)} /></div>
                  <div className="space-y-1.5"><Label>Relationship</Label><Input data-testid={`dependent-relationship-${i}`} placeholder="Child, parent…" value={dep.relationship} onChange={(e) => updateDependent(i, "relationship", e.target.value)} /></div>
                  <div className="space-y-1.5 col-span-2 sm:col-span-1"><Label>Date of birth</Label><Input data-testid={`dependent-dob-${i}`} type="date" value={dep.dob} onChange={(e) => updateDependent(i, "dob", e.target.value)} /></div>
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeDependent(i)} data-testid={`dependent-remove-${i}`}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addDependent} data-testid="add-dependent-button">
                <Plus className="w-4 h-4 mr-1" /> Add dependent
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="step-income">
              <h2 className="font-heading text-2xl font-semibold">Income Sources</h2>
              <p className="text-sm text-muted-foreground">Check all that applied to you this tax year.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {INCOME_SOURCES.map((s) => (
                  <label key={s.key} className="flex items-center gap-3 border border-border rounded-md p-3.5 cursor-pointer hover:bg-muted/50 transition-colors" data-testid={`income-source-${s.key}`}>
                    <Checkbox checked={income.sources.includes(s.key)} onCheckedChange={() => toggleSource(s.key)} data-testid={`income-checkbox-${s.key}`} />
                    <span className="text-sm">{s.label}</span>
                  </label>
                ))}
              </div>
              <div className="space-y-1.5">
                <Label>Additional income details (optional)</Label>
                <Textarea data-testid="income-details" rows={3} placeholder="Employer names, side businesses, crypto sales…" value={income.details} onChange={(e) => setIncome((i) => ({ ...i, details: e.target.value }))} />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="step-prior-year">
              <h2 className="font-heading text-2xl font-semibold">Prior Year Filing</h2>
              <div className="space-y-1.5">
                <Label>Did you file a tax return last year?</Label>
                <Select value={priorYear.filedLastYear} onValueChange={(v) => setPriorYear((p) => ({ ...p, filedLastYear: v }))}>
                  <SelectTrigger data-testid="prior-filed-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="not_required">Not required to file</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Result of last year's return</Label>
                <Select value={priorYear.result} onValueChange={(v) => setPriorYear((p) => ({ ...p, result: v }))}>
                  <SelectTrigger data-testid="prior-result-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="refund">Received a refund</SelectItem>
                    <SelectItem value="owed">Owed taxes</SelectItem>
                    <SelectItem value="even">Broke even</SelectItem>
                    <SelectItem value="unknown">Not sure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Who prepared your last return? (optional)</Label><Input data-testid="prior-preparer" placeholder="Self, another preparer…" value={priorYear.preparer} onChange={setPY("preparer")} /></div>
              <div className="space-y-1.5"><Label>Anything we should know? (optional)</Label><Textarea data-testid="prior-notes" rows={3} value={priorYear.notes} onChange={setPY("notes")} /></div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4" data-testid="step-signature">
              <h2 className="font-heading text-2xl font-semibold">E-Signature</h2>
              <p className="text-sm text-muted-foreground">
                By signing, you authorize {`Laronta Pro Tax Services LLC`} to prepare your tax return using the information provided, and certify it is accurate to the best of your knowledge.
              </p>
              <div className="space-y-1.5">
                <Label>Type your full legal name</Label>
                <Input data-testid="signature-name" value={signature.fullName} onChange={(e) => setSignature((s) => ({ ...s, fullName: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>Sign below</Label>
                <SignaturePad onChange={(dataUrl) => setSignature((s) => ({ ...s, dataUrl }))} />
              </div>
              <label className="flex items-start gap-3 cursor-pointer" data-testid="signature-consent-label">
                <Checkbox checked={signature.agreed} onCheckedChange={(v) => setSignature((s) => ({ ...s, agreed: !!v }))} data-testid="signature-consent-checkbox" />
                <span className="text-sm text-muted-foreground">I agree to the terms above and consent to sign electronically.</span>
              </label>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-4" data-testid="step-documents">
              <h2 className="font-heading text-2xl font-semibold">Document Upload</h2>
              <p className="text-sm text-muted-foreground">Upload W-2s, 1099s, photo ID, prior returns, or any other important documents (PDF, Word, JPG/PNG images — max 15MB each).</p>
              <div className="flex gap-3 items-end">
                <div className="space-y-1.5 flex-1">
                  <Label>Document type</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger data-testid="doc-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DOC_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="upload-document-button">
                  <Upload className="w-4 h-4 mr-1" /> Upload file
                </Button>
                <input ref={fileInputRef} type="file" data-testid="doc-file-input" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.txt,.csv,.heic" onChange={uploadFile} />
              </div>
              <div className="space-y-2" data-testid="uploaded-documents-list">
                {documents.length === 0 && <p className="text-sm text-muted-foreground">No documents uploaded yet.</p>}
                {documents.map((d) => (
                  <div key={d.file_id} className="flex items-center gap-3 border border-border rounded-md p-3" data-testid={`doc-row-${d.file_id}`}>
                    <FileText className="w-4 h-4 text-primary" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{d.original_filename}</p>
                      <p className="text-xs text-muted-foreground capitalize">{d.category.replace("_", " ")} · {(d.size / 1024).toFixed(0)} KB</p>
                    </div>
                    <button type="button" onClick={() => openFile(d.file_id)} data-testid={`doc-download-${d.file_id}`} title="Open document">
                      <Download className="w-4 h-4 text-muted-foreground hover:text-primary transition-colors" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-between mt-8 pt-6 border-t border-border">
            <Button type="button" variant="ghost" disabled={step === 0} onClick={() => setStep((s) => s - 1)} data-testid="wizard-back-button">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button type="button" onClick={() => setStep((s) => s + 1)} data-testid="wizard-next-button">
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button type="button" onClick={submit} disabled={submitting || !signature.agreed || !signature.fullName} data-testid="wizard-submit-button">
                {submitting ? "Submitting…" : submitted ? "Update Intake" : "Submit Intake"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StaffIntakes() {
  const { user } = useAuth();
  const isAdminUser = user?.role === "admin";
  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [docsClient, setDocsClient] = useState(null);
  const [docs, setDocs] = useState([]);
  const [onlineForms, setOnlineForms] = useState([]);
  const [detailClient, setDetailClient] = useState(null);

  const load = () => {
    api.get("/intakes").then((r) => setIntakes(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openDocs = (intake) => {
    setDocsClient(intake);
    setDocs([]);
    setOnlineForms([]);
    api.get(`/documents?user_id=${intake.user_id}`).then((r) => setDocs(r.data)).catch((err) => toast.error(formatApiError(err)));
    api.get(`/completed-forms?user_id=${intake.user_id}`).then((r) => setOnlineForms(r.data)).catch(() => {});
  };

  const deleteDoc = async (fileId) => {
    try {
      await api.delete(`/documents/${fileId}`);
      setDocs((d) => d.filter((x) => x.file_id !== fileId));
      toast.success("Document deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setStatus = async (userId, status) => {
    try {
      await api.patch(`/intakes/${userId}/status`, { status });
      toast.success("Status updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="staff-intakes-page" className="animate-fade-up">
      <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-medium">Staff view</p>
      <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight mt-1">Client Intakes</h1>
      <Card className="mt-8">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
          ) : intakes.length === 0 ? (
            <p className="text-sm text-muted-foreground p-8" data-testid="no-intakes-message">No intakes submitted yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="intakes-table">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="p-4 font-medium text-muted-foreground">Client</th>
                    <th className="p-4 font-medium text-muted-foreground">Filing Status</th>
                    <th className="p-4 font-medium text-muted-foreground">Submitted</th>
                    <th className="p-4 font-medium text-muted-foreground">Signed</th>
                    <th className="p-4 font-medium text-muted-foreground">Documents</th>
                    <th className="p-4 font-medium text-muted-foreground">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {intakes.map((i) => (
                    <tr key={i.user_id} className="border-b border-border last:border-0" data-testid={`staff-intake-row-${i.user_id}`}>
                      <td className="p-4">
                        <p className="font-medium">{i.client_name}</p>
                        <p className="text-xs text-muted-foreground">{i.email}</p>
                      </td>
                      <td className="p-4 capitalize">{i.personal?.filingStatus?.replace(/_/g, " ") || "—"}</td>
                      <td className="p-4 text-muted-foreground">{i.submitted_at?.slice(0, 10)}</td>
                      <td className="p-4">{i.signature?.fullName ? <span className="text-primary text-xs font-medium">Signed: {i.signature.fullName}</span> : "—"}</td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => setDetailClient({ user_id: i.user_id, client_name: i.client_name, email: i.email })} data-testid={`staff-detail-button-${i.user_id}`}>
                            <Eye className="w-3.5 h-3.5 mr-1" /> Details
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openDocs(i)} data-testid={`staff-docs-button-${i.user_id}`}>
                            <FileText className="w-3.5 h-3.5 mr-1" /> View Files
                          </Button>
                        </div>
                      </td>
                      <td className="p-4">
                        <Select value={i.status} onValueChange={(v) => setStatus(i.user_id, v)}>
                          <SelectTrigger className="w-36" data-testid={`intake-status-select-${i.user_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="submitted">Submitted</SelectItem>
                            <SelectItem value="in_review">In Review</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!docsClient} onOpenChange={(o) => !o && setDocsClient(null)}>
        <DialogContent data-testid="staff-docs-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Documents — {docsClient?.client_name}</DialogTitle>
            <DialogDescription>Files uploaded by this client with their intake. Click Open to view in a new tab.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2" data-testid="staff-docs-list">
            {docs.length === 0 && <p className="text-sm text-muted-foreground">No documents uploaded by this client.</p>}
            {docs.map((d) => (
              <div key={d.file_id} className="flex items-center gap-3 border border-border rounded-md p-3" data-testid={`staff-doc-${d.file_id}`}>
                <FileText className="w-4 h-4 text-primary" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{d.original_filename}</p>
                  <p className="text-xs text-muted-foreground capitalize">{d.category.replace("_", " ")} · {(d.size / 1024).toFixed(0)} KB</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => openFile(d.file_id)} data-testid={`staff-doc-open-${d.file_id}`}>Open</Button>
                {isAdminUser && (
                  <button onClick={() => deleteDoc(d.file_id)} data-testid={`staff-doc-delete-${d.file_id}`} title="Delete document"
                    className="text-muted-foreground hover:text-destructive transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-medium mt-5">Online Form Submissions</p>
          <div className="space-y-2 mt-2" data-testid="staff-online-forms-list">
            {onlineForms.length === 0 && <p className="text-sm text-muted-foreground">None submitted online.</p>}
            {onlineForms.map((f) => (
              <details key={f.entry_id} className="border border-border rounded-md p-3" data-testid={`staff-online-form-${f.entry_id}`}>
                <summary className="text-sm font-medium cursor-pointer">{f.form_title} · {f.submitted_at?.slice(0, 10)}</summary>
                <dl className="mt-2 space-y-1">
                  {Object.entries(f.data || {}).map(([k, v]) => (
                    <div key={k} className="flex gap-2 text-xs">
                      <dt className="text-muted-foreground capitalize w-36 shrink-0">{k.replace(/_/g, " ")}</dt>
                      <dd className="break-words">{typeof v === "boolean" ? (v ? "Yes" : "No") : String(v)}</dd>
                    </div>
                  ))}
                </dl>
              </details>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <ClientDetailDialog client={detailClient} open={!!detailClient} onOpenChange={(o) => !o && setDetailClient(null)} />
    </div>
  );
}
